import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Button,
  Popconfirm,
  Form,
  Space,
  Col,
  Row,
  message,
  Select,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { ReactComponent as SaveIcon } from "../../assets/save-icon.svg";
import { ReactComponent as CrossIcon } from "../../assets/cross-icon.svg";
import SpinnerComponent from "../../components/spinner";
import { arrayMoveImmutable } from "array-move";
import CheckSvg from "../../assets/check.svg.png";
import DragDropIcon from "../../assets/dragdropicon.png";
import {
  createMaterialTypes,
  deleteMaterialTypes,
  fetchMaterialTypes,
  isAddingAction,
  updateMaterialTypesOrder,
  updateMaterialTypes,
} from "./redux/actions";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";

const { Option, OptGroup } = Select;

const UOM_CATEGORIES_AND_UNITS = {
  "Weight-Based Units": {
    "t": "Tons",
    "kg": "Kilogram",
    "g": "Gram",
    "oz": "Once",
    "lb": "Pound"
  },
  "Volume-Based Units": {
    "m³": "Cubic Meter",
    "cm³": "Cubic Centimetre",
    "mm³": "Cubic millimetre",
    "dm³": "Cubic Decimetre",
    "l": "Litre",
    "ml": "Millilitre",
    "yd³": "Cubic Yard"
  },
  "Length-Based Units": {
    "m": "meter",
    "cm": "Centimetre",
    "mm": "Millimetre",
    "km": "Kilometre",
    "yd²": "Yard",
    "ft": "Feet",
    "in": "Inch",
    "mi": "Mile",
    "lm": "Linear Meter",
    "bf": "Broad Feet"
  },
  "Area-Based Units": {
    "m²": "Square Meter",
    "cm²": "Square Centimetre",
    "mm²": "Square Millimetre",
    "ha": "Hectare",
    "yd²": "Square Yard"
  },
  "Piece-Based Units": {
    "Ea.": "Each",
    "No.": "Number",
    "SUM": "Sum",
    "Lot": "Lot",
    "Lump Sum": "Lump Sum",
    "set": "Set",
    "Box": "Box",
    "bdl": "Bundle",
    "pk": "Pack",
    "bbl.": "Barrel"
  },
  "Time-Based Units": {
    "hr": "Hour",
    "d": "Day",
    "wk.": "Week",
    "MTh": "Month",
    "Yr": "Year"
  },
  "Specialized Units": {
    "°": "Degrees",
    "kN": "Kilonewton",
    "kN.m": "Kilonewton-meter",
    "m³/km": "Cubic Meters per Kilometre"
  },
  "Electrical/Technical Units": {
    "kWh": "Kilowatt-hour",
    "A": "Ampere",
    "V": "Volt",
    "kW": "Kilowatt",
    "VA": "Volt Ampere",
    "Ω": "Ohm",
    "Hz": "Hertz",
    "lx": "Lux"
  },
  "Flow or Coverage Units": {
    "m²/l": "Square Meter per Litre",
    "m³/h": "Cubic Meter per Hour",
    "m³/s": "Cubic Meter per Second"
  },
  "Mechanical & Piping Units": {
    "bar": "Bar (Pressure)",
    "Pa": "Pascal (Pressure)",
    "NB": "Nominal Bore",
    "Ø": "Diameter",
    "kg/m": "Kilogram per Meter"
  },
  "Control & Instrumentation Units": {
    "mbar": "Millibar",
    "ppm": "Parts per Million",
    "%": "Percentage",
    "°C": "Degree Celsius",
    "m/s": "Meter per Second",
    "RPM": "Revolutions per Minute"
  }
};

const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);
const DragHandle = SortableHandle(() => <img alt="" src={DragDropIcon} />);

const MaterialTypesTable = ({ canUpdate }) => {
  const [editingLoading, setEditingLoading] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [data, setData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const isEditing = (record) => record.id === editingKey;

  const { allMaterialTypes, fetchingLoading, updateLoading, isAdding } =
    useSelector((state) => state.materialTypesReducer);

  useEffect(() => {
    dispatch(fetchMaterialTypes());

    return () => {
      dispatch(isAddingAction(false));
    };
  }, []);

  useEffect(() => {
    setEditingLoading(updateLoading);
    if (!updateLoading) {
      setEditingKey("");
    }
  }, [updateLoading]);

  useEffect(() => {
    setData(allMaterialTypes);
  }, [allMaterialTypes]);

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const textInput = (
      <Input
        placeholder="Insert Material Type"
        style={{ width: '100px', height: '35px' }}
        className="material-type"
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            save(record.id);
          }
        }}
      />
    );

    // UOM Category dropdown
    const categoryDropdown = (
      <Select 
        placeholder="Select UOM Category"
        style={{ width: '200px' }}
        className="uom-category-dropdown"
        onChange={(value) => {
          setSelectedCategory(value);
          // Reset UOM when category changes
          form.setFieldsValue({ UOM: undefined });
        }}
      >
        {Object.keys(UOM_CATEGORIES_AND_UNITS).map(category => (
          <Option key={category} value={category}>{category}</Option>
        ))}
      </Select>
    );

    // UOM dropdown that depends on selected category
    const uomDropdown = (
      <Select
        placeholder="Select UOM"
        style={{ width: '200px' }}
        className="uom-dropdown"
        disabled={!form.getFieldValue('uomCategory')}
      >
        {form.getFieldValue('uomCategory') && 
          Object.entries(UOM_CATEGORIES_AND_UNITS[form.getFieldValue('uomCategory')] || {}).map(([key, value]) => (
            <Option key={key} value={key}>{`${key} - ${value}`}</Option>
          ))
        }
      </Select>
    );

    let inputNode = null;

    if (dataIndex === "uom") {
      inputNode = uomDropdown;
    } else if (dataIndex === "uomCategory") {
      inputNode = categoryDropdown;
    }

    if (dataIndex === "materialType") {
      return (
        <td {...restProps}>
          {editing ? (
            <Form.Item
              name={dataIndex}
              style={{
                margin: 0,
              }}
              rules={[
                {
                  required: true,
                  message: `This field is required!`,
                },
                {
                  min: 1,
                  message: `invalid!`,
                },
              ]}
            >
              {textInput}
            </Form.Item>
          ) : (
            children
          )}
        </td>
      );
    }
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{
              margin: 0,
            }}
            rules={[
              {
                required: true,
                message: `Required!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const edit = (record) => {
    dispatch(isAddingAction(true));
    
    // If record has UOMCategory, set the selected category for dropdown filtering
    if (record.uomCategory) {
      setSelectedCategory(record.uomCategory);
    }

    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.id);
  };

  const cancel = (record) => {
    dispatch(isAddingAction(false));
    form.resetFields();
    setEditingKey("");
    setSelectedCategory(null);
    if (record?.isAdd) {
      return setData((data) => data.filter((el) => el.id !== record.id));
    } else {
      setData(data);
    }
  };

  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const newData = [...data];
      const index = newData.findIndex((item) => id === item.id);
      if (index > -1) {
        const item = newData[index];
        if (item?.isAdd) {
          form.submit();
        } else {
          if (isAlreadyExist({ ...item, ...row })) {
            return message.error("Material Type already exist!");
          }

          dispatch(
            updateMaterialTypes({
              ...item,
              ...row,
            })
          );
        }
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
      if (errInfo?.values?.materialType === "") {
        message.error("Material Type should not be blank!");
      }
      if (errInfo?.values?.uomCategory === "") {
        message.error("UOM Category should not be blank!");
      }
    }
  };

  const handleDelete = (record) => {
    setEditingKey(record.id);
    dispatch(deleteMaterialTypes(record));
  };

  const handleAdd = () => {
    dispatch(isAddingAction(true));
    let id = Math.floor(Math.random() * 1122123456);
    const newData = {
      id,
      uomCategory: undefined,
      materialType: "",
      uom: undefined,
      isAdd: true,
    };
    setData((data) => [...data, newData]);
    setEditingKey(id);
    edit(newData);
  };

  const isAlreadyExist = (record) => {
    const { materialType } = record;
    const alreadyExist = data.filter(
      (el) =>
        el.materialType?.trim()?.toLowerCase() ===
        materialType?.trim()?.toLowerCase()
    );

    return alreadyExist.length > 0 && record.id !== alreadyExist[0]?.id
      ? true
      : false;
  };

  const handleFinish = (values) => {
    console.log("values",values);
    const { uomCategory, materialType, uom } = values;

    if (isAlreadyExist(values)) {
      return message.error("Material Type already exists!");
    }

    // Calculate the position based on the current data length
    const position = data.length;

    dispatch(
      createMaterialTypes({
        uom,
        materialType: materialType.trim(),
        uomCategory,
        id: editingKey,
        position,
      })
    );
  };

  const columns = [
    {
      title: "Material Type",
      dataIndex: "materialType",
      key: "materialType",
      width: "20%",
      editable: true,
    },
    {
      title: "UOM Category",
      dataIndex: "uomCategory",
      key: "uomCategory",
      width: "20%",
      editable: true,
      render: (uomCategory) => uomCategory || "-",
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: "25%",
      editable: true,
      render: (uom, record) => {
        if (!uom) return "-";
        
        // Find the category and description for this UOM
        const category = record.uomCategory;
        if (category && UOM_CATEGORIES_AND_UNITS[category] && UOM_CATEGORIES_AND_UNITS[category][uom]) {
          return `${uom} - ${UOM_CATEGORIES_AND_UNITS[category][uom]}`;
        }
        
        return uom;
      },
    },
    {
      title: "",
      key: "action",
      dataIndex: "",
      width: "9%",
      align: "left",
      render: (_, record) => {
        if (!canUpdate) {
          return;
        }

        const { status_type } = record;
        let canDelete = status_type === "not-selected" ? false : true;

        const editable = isEditing(record);
        return editable ? (
          editingLoading && editingKey === record.id ? (
            <SpinnerComponent fontSize={14} />
          ) : (
            <Space size={20} align="center">
              <SaveIcon
                className="custom-icon"
                onClick={() => save(record.id)}
              />
              <Popconfirm
                title="Are you sure you want to cancel?"
                onConfirm={() => cancel(record)}
                okText="Yes"
                cancelText="No"
              >
                <CrossIcon
                  style={{ height: "20px", width: "20px" }}
                  className="custom-icon"
                />
              </Popconfirm>
            </Space>
          )
        ) : (
          <Space size={20} align="center">
            <EditIcon
              className={`custom-icon ${isAdding ? "disable-icon" : ""}`}
              disabled={editingKey !== ""}
              onClick={() => !isAdding && edit(record)}
            />
            {canDelete &&
              data.length >= 1 &&
              (isAdding ? (
                <DeleteIcon className={`custom-icon disable-icon`} />
              ) : (
                <Popconfirm
                  title="Are you sure you want to delete this Material Type?"
                  onConfirm={() => handleDelete(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteIcon className="custom-icon" />
                </Popconfirm>
              ))}
          </Space>
        );
      },
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: "9%",
      className: "drag-visible",
      render: (order, record) => {
        const { status_type } = record;
        let canDelete = status_type === "not-selected" ? false : true;
        return !Boolean(editingKey) && canDelete && canUpdate && <DragHandle />;
      },
    },
  ];

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const { status_type } = data[newIndex];

    let shouldNotReorder = status_type !== "not-selected" ? false : true;

    if (shouldNotReorder) {
      return setData((prevData) => prevData);
    }

    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        data.slice(),
        oldIndex,
        newIndex
      ).filter((el) => !!el);

      dispatch(updateMaterialTypesOrder(newData));
    }
  };

  const DraggableContainer = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    const index = data.findIndex(
      (x) => x.position == restProps["data-row-key"]
    );

    return <SortableItem index={index} {...restProps} />;
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        // title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <Row className="production-manager-parent-row card-container">
      <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
        <Row align="middle" gutter={[30, 0]}>
          <Col>
            <div>
              <span className="heading-span">Material Type Manager</span>
            </div>
          </Col>
        </Row>
      </Col>

      <Col lg={23} xl={18} xxl={17} md={24}>
        <Form
          form={form}
          component={false}
          onFinish={handleFinish}
          className="table-style"
        >
          <style>
            {`
              .ant-input {
                padding-left: 12px !important;
              }
              .ant-select-selector {
                padding-left: 12px !important;
              }
              .ant-select-selection-item {
                padding-left: 0px !important;
              }
              /* Target the actual input element inside ant-input */
              input.ant-input {
                text-indent: 8px;
              }
            `}
          </style>
          <Table
            className="special-table activity-type-table project-statuses-table"
            components={{
              body: {
                cell: EditableCell,
                wrapper: DraggableContainer,
                row: DraggableBodyRow,
              },
            }}
            scroll={{ x: !fetchingLoading }}
            rowClassName="editable-row"
            dataSource={data}
            loading={fetchingLoading}
            columns={mergedColumns}
            pagination={false}
            rowKey="position"
          />
          <Col offset={1} lg={24} xl={18} xxl={17} md={24} className="table">
            {canUpdate && (
              <Button
                icon={<PlusOutlined/>}
                className="add-user-btn"
                size="middle"
                disabled={isAdding}
                onClick={handleAdd}
                style={{
                  marginTop: "20px",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                TYPE
              </Button>
            )}
          </Col>
        </Form>
      </Col>
    </Row>
  );
};

export default MaterialTypesTable;
